<template>
  <div class="workflow-designer">
    <VueFlow
      :nodes="nodes"
      :edges="edges"
      :nodes-draggable="true"
      :nodes-connectable="false"
      :edges-updatable="false"
      :elements-selectable="true"
      :zoom-on-double-click="false"
      :min-zoom="0.2"
      :max-zoom="1.5"
      @node-click="onNodeClick"
      @node-drag-stop="onNodeDragStop"
    >
      <template #node-start="{ data }"><StartNode :data="data" /></template>
      <template #node-step="{ id, data }"><StepNode :id="id" :data="data" @ports-measured="onPortsMeasured" /></template>
      <template #node-external="{ data }"><ExternalNode :data="data" /></template>
    </VueFlow>

    <PillButton class="wf-add-step" text="Add Step" icon="add_circle_filled" primary elevated @click="$emit('addStep')" />
  </div>
</template>

<script>
import { VueFlow, useVueFlow } from '@vue-flow/core';
import '@vue-flow/core/dist/style.css';
import '@vue-flow/core/dist/theme-default.css';
import './workflow-designer.css';
import { mapGetters, mapMutations } from 'vuex';
import buildWorkflowGraph from './graph';
import StartNode from './nodes/StartNode.vue';
import StepNode from './nodes/StepNode.vue';
import ExternalNode from './nodes/ExternalNode.vue';

export default {
  name: 'WorkflowDesigner',
  components: { VueFlow, StartNode, StepNode, ExternalNode },
  props: {
    steps: { type: Array, default: () => [] },
  },
  emits: ['startClick', 'addStep'],
  setup() {
    // fit once, after nodes have real dimensions — but never on later
    // re-initialisations (e.g. after a drag) so the viewport isn't yanked around.
    const { fitView, onNodesInitialized } = useVueFlow();
    let fitted = false;
    onNodesInitialized(() => {
      if (fitted) return;
      fitted = true;
      fitView({ padding: 0.2 });
    });
    return { fitView };
  },
  data() {
    return { nodes: [], edges: [], extPositions: {}, draggedExternals: {} };
  },
  computed: {
    ...mapGetters(['getProjectFlows']),
  },
  methods: {
    ...mapMutations(['SET_SELECTED_INTENT_DEFINITION_STEP_ID', 'SET_SELECTED_INTENT_DEFINITION_STEP_XY']),
    flowsMap() {
      const map = {};
      const flows = this.getProjectFlows() || {};
      Object.keys(flows).forEach((key) => {
        map[key] = flows[key].name || key;
      });
      return map;
    },
    rebuild() {
      const { nodes, edges } = buildWorkflowGraph(this.steps || [], this.flowsMap(), this.extPositions);
      this.nodes = nodes;
      this.edges = edges;
    },
    // Align each external endpoint node to the vertical position of the block it
    // originates from, so its connector doesn't cross over other lines.
    onPortsMeasured({ nodeId, ports }) {
      const stepNode = this.nodes.find((n) => n.id === nodeId);
      if (!stepNode) return;
      let changed = false;
      ports.forEach((port) => {
        const ext = this.nodes.find((n) => n.id === `ext-${port.id}`);
        if (ext && !this.draggedExternals[ext.id]) {
          const y = Math.round(stepNode.position.y + port.topNum - 22);
          if (ext.position.y !== y) {
            ext.position = { x: ext.position.x, y };
            changed = true;
          }
        }
      });
      if (changed) this.nodes = [...this.nodes];
    },
    onNodeClick(payload) {
      const node = payload && payload.node;
      if (!node) return;
      if (node.type === 'start') {
        this.$emit('startClick');
      } else if (node.type === 'step') {
        this.SET_SELECTED_INTENT_DEFINITION_STEP_ID(node.id);
      }
    },
    onNodeDragStop(payload) {
      const node = payload && payload.node;
      if (!node || !node.position) return;
      const x = Math.round(node.position.x);
      const y = Math.round(node.position.y);
      if (node.type === 'step') {
        this.SET_SELECTED_INTENT_DEFINITION_STEP_XY({ id: node.id, x, y });
      } else if (node.type === 'external') {
        // external nodes aren't part of the saved structure; keep their dragged
        // position for the session so rebuilds don't snap them back.
        this.draggedExternals[node.id] = true;
        this.extPositions[node.id] = { x, y };
      }
    },
  },
  watch: {
    steps: {
      handler() {
        this.rebuild();
      },
      deep: true,
    },
  },
  created() {
    this.rebuild();
  },
};
</script>
